import { Component, Inject } from "@angular/core";
import { v4 } from "uuid";
import {
  ToastController,
  NavController,
  ViewController,
  NavParams
} from "ionic-angular";
import { AuthService } from "../../app/services/auth.service";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs/Subscription";
import { UserState } from "../../app/store/reducers/user.reducer";
import { AppState } from "../../app/models/app.state";
import { FhirService } from "../../app/services/fhir.service";
import * as PatientActions from "../../app/store/actions/patient.actions";
import { AdminService } from "../../app/services/admin.service";
import { OnDestroy } from "@angular/core/src/metadata/lifecycle_hooks";
import { notNullOrUndefined } from "../../app/utils/not-null-or-undefined";
import {
  ENV_VARS,
  EnvironmentVariables
} from "../../app/environment-variables/environment-variables.module";
import { generatePassword } from "../../app/utils/generate-password";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { DashboardService } from "../../app/services/dashboard.service";
import {
  IDiaprofilHcp,
  twoFactorMethodOptions,
  staffTypeOptions
} from "../../app/models/types";
import * as moment from "moment";
export function uuid(): string {
  return v4().replace(/-/g, "");
}

@Component({
  selector: "manage-hcp-user-modal",
  templateUrl: "manage-hcp-user.modal.html"
})
export class ManageHcpUserModal implements OnDestroy {
  userForm: FormGroup;
  userSubscription: Subscription;
  userState: UserState;
  hcp: IDiaprofilHcp;
  modalType: "EDIT" | "CREATE";
  _i18n_str = _i18n_str;
  twoFactorOptions = twoFactorMethodOptions;
  staffTypes = staffTypeOptions;
  is2faEnabled: boolean;
  constructor(
    public navCtrl: NavController,
    public viewCtrl: ViewController,
    private navParams: NavParams,
    public auth: AuthService,
    public fhir: FhirService,
    public admin: AdminService,
    private toastCtrl: ToastController,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private modalCtrl: ModalController,
    public dashboard: DashboardService,
    @Inject(ENV_VARS) public envVars: EnvironmentVariables
  ) {
    this.is2faEnabled = ![null, "DISABLED"].includes(
      this.admin.orgConfig.ffHcp2faLogin
    );
    this.userSubscription = this.store
      .select(state => state.user)
      .subscribe(user => (this.userState = user));
    this.modalType = this.navParams.get("modalType") || "CREATE";
    this.hcp =
      (this.navParams.get("user") as IDiaprofilHcp) || <IDiaprofilHcp>{};
    this.userForm = this.fb.group({
      fullName: [this.hcp.fullName],
      userName:
        this.modalType === "CREATE"
          ? [null, Validators.compose([Validators.required, Validators.email])]
          : undefined,
      password:
        this.modalType === "CREATE"
          ? [
              null,
              Validators.compose([Validators.required, Validators.minLength(6)])
            ]
          : undefined,
      email: [
        this.hcp.email,
        Validators.compose([Validators.required, Validators.email])
      ],
      mobileNumber: [
        this.hcp.mobileNumber,
        Validators.compose([
          Validators.pattern(
            /^((\(?\+45\)?)?)(\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2})$/
          )
        ])
      ],
      staffType: [
        this.hcp.staffType,
        Validators.compose([Validators.required])
      ],
      twoFactorMethods: this.is2faEnabled && this.modalType === "CREATE"
        ? [this.hcp.twoFactorMethods, Validators.compose([Validators.required])]
        : undefined
    });
  }

  generateRandomCpr() {
    const randomDate = (start, end) => {
      return new Date(
        start.getTime() + Math.random() * (end.getTime() - start.getTime())
      );
    };
    const date = randomDate(new Date("1950-01-01"), new Date("2020-01-01"));
    const cprStart = moment(date).format("MMDDYY");
    const cprEnd = Math.floor(Math.random() * 9000 + 1)
      .toString()
      .padStart(4, "0");
    const cpr = cprStart + cprEnd;
    return cpr;
  }

  createFhirTelecomForMobileNumber(mobileNumber): fhir.ContactPoint {
    const fhirAddress: fhir.ContactPoint = {
      system: "phone",
      use: "mobile",
      value: mobileNumber
    };
    return fhirAddress;
  }

  createHcp() {
    const formValues = this.userForm.value;
    const userUuid = uuid();
    this.fhir
      .createUser({
        username: formValues.userName,
        password: formValues.password,
        mobile_number: formValues.mobileNumber,
        email: formValues.email,
        fullname: formValues.fullName,
        user_type: "Practitioner",
        user_uid: userUuid,
        organization_uid: this.userState.organizationUuid,
        gender: "male",
        birthdate: "2000-01-01",
        social_security_number: this.generateRandomCpr(),
        telecom:
          formValues.mobileNumber !== "" && formValues.mobileNumber !== null
            ? [this.createFhirTelecomForMobileNumber(formValues.mobileNumber)]
            : [],
        properties: {
          staffType: formValues.staffType,
          twofactor_enabled: this.is2faEnabled,
          cell_phone_number: formValues.mobileNumber,
          twofactor_messaging_methods: this.is2faEnabled
            ? formValues.twoFactorMethods
            : undefined
        }
      })
      .subscribe(
        data => {
          this.viewCtrl.dismiss();
          const toast = this.toastCtrl.create({
            message: `Bruger oprettet - husk at password ikke sendes automatisk til brugeren!`,
            duration: 5000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          toast.present();
        },
        // Errors will call this callback instead:
        err => {}
      );
  }

  patchHcp() {
    const formValues = this.userForm.value;
    this.fhir
      .patchUser(this.hcp.userName, {
        mobile_number: formValues.mobileNumber,
        email: formValues.email,
        fullname: formValues.fullName,
        telecom:
          formValues.mobileNumber !== "" && formValues.mobileNumber !== null
            ? [this.createFhirTelecomForMobileNumber(formValues.mobileNumber)]
            : [],
        properties: {
          staffType: formValues.staffType,
          cell_phone_number: formValues.mobileNumber
        }
      })
      .subscribe(
        result => {
          this.viewCtrl.dismiss();
          const toast = this.toastCtrl.create({
            message: `Bruger updateret!`,
            duration: 5000,
            position: "top",
            closeButtonText: "Luk",
            showCloseButton: true
          });
          toast.present();
        },
        // Errors will call this callback instead:
        err => {}
      );
  }

  dismiss() {
    this.viewCtrl.dismiss();
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/modals/manage-hcp-user.modal/manage-hcp-user.modal.ts